import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import services from "../../../services";
import { useForm, useWatch } from "react-hook-form";
import { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import BarLoading from "../../../components/Loading/BarLoading";
import { yupResolver } from "@hookform/resolvers/yup";
import { CategoryCreateAndUpdate } from "../validations";
import { Switch, rem } from "@mantine/core";
import { IconCheck } from "@tabler/icons-react";
import { IconX } from "@tabler/icons-react";
import SaveButton from "../../../components/Buttons/SaveButton";
import CustomInput from "../../../components/CustomInput/CustomInput";

const ProductCardTypeCreate = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  let { id } = useParams();
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    control,

    formState: { errors },
  } = useForm({
    resolver: yupResolver(CategoryCreateAndUpdate),
    defaultValues: {
      name: "",
      parent_id: null,
      is_active: true,
    },
  });

  const addProductCardType = useMutation({
    mutationFn: (newProductCardType) =>
      services.createProductCardType(newProductCardType),
  });
  const updateProductCardType = useMutation({
    mutationFn: (newProductCardType) =>
      services.updateProductCardType(id, newProductCardType),
  });

  const onSubmit = async (data) => {
    let newProductCardTypePayload;
    let productCardType;

    try {
      newProductCardTypePayload = {
        ...data,
      };
      if (!id) {
        productCardType = await addProductCardType.mutateAsync(
          newProductCardTypePayload
        );
      } else {
        productCardType = await updateProductCardType.mutateAsync(
          newProductCardTypePayload
        );
      }

      if (!id && productCardType) {
        toast.success(
          productCardType?.data?.message
            ? productCardType?.data?.message
            : t("notifications.success.product-card-type-create"),
          {
            duration: 3000,
          }
        );

        reset();
        handleCancel();
      } else if (id && productCardType) {
        toast.success(
          productCardType?.data?.message
            ? productCardType?.data?.message
            : t("notifications.success.product-card-type-update"),
          {
            duration: 3000,
          }
        );
        handleCancel();
      }
    } catch (e) {
      toast.error(
        productCardType?.data?.message
          ? productCardType?.data?.message
          : t("notifications.error.message"),
        {
          duration: 3000,
        }
      );
    }
  };

  const handleCancel = () => {
    reset();
    navigate("/product/card-type/list");
  };

  const fetchCardType = useCallback(async () => {
    if (id) {
      setLoading(true);
      try {
        const productCardType = await services.getProductCardTypeById(id);
        Object.keys(productCardType.data).forEach((key) => {
          setValue(key, productCardType.data[key]);
        });
      } catch (e) {
        navigate("/product/card-type/list");
        toast.error(t("notifications.error.product-card-type-notfound"), {
          duration: 3000,
        });
      } finally {
        setLoading(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    reset();

    if (id) {
      fetchCardType();
    }
  }, [id]);

  const isActive = useWatch({
    control,
    name: "is_active",
    defaultValue: true,
  });
  return (
    <>
      {!loading ? (
        <div className="px-4 sm:px-6 lg:px-8">
          <form onSubmit={handleSubmit((data) => onSubmit(data))}>
            <div className="space-y-12">
              <div className="border-b border-white/10 pb-12">
                <h2 className="text-base font-semibold leading-7 dark:text-white text-slate-700">
                  {id
                    ? t("titles.product-card-type-edit")
                    : t("titles.product-card-type-create")}
                </h2>
                <p className="mt-1 text-sm leading-6 text-gray-400">
                  {id
                    ? t("descriptions.product-card-type-edit")
                    : t("descriptions.product-card-type-create")}
                </p>

                <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 max-w-full">
                  <div className="sm:col-span-full">
                    <CustomInput
                      label={t("product.card-type-name")}
                      register={register}
                      name="name"
                      errors={errors}
                      placeholder={t("product.card-type-name")}
                    />
                  </div>

                  <div className="sm:col-span-full">
                    <label
                      htmlFor="first-name"
                      className="block text-sm font-medium leading-6 dark:text-white text-slate-700"
                    >
                      {t("labels.is-active")}
                    </label>
                    <div className="mt-2">
                      <Switch
                        checked={isActive}
                        onChange={(event) =>
                          setValue("is_active", event.target.checked)
                        }
                        color="teal"
                        size="lg"
                        thumbIcon={
                          isActive ? (
                            <IconCheck
                              style={{ width: rem(12), height: rem(12) }}
                              color="green"
                              stroke={3}
                            />
                          ) : (
                            <IconX
                              style={{ width: rem(12), height: rem(12) }}
                              color="red"
                              stroke={3}
                            />
                          )
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-6 flex items-center justify-end gap-x-6">
              <button
                type="button"
                onClick={handleCancel}
                className="text-sm font-semibold leading-6 dark:text-white text-slate-700"
              >
                {t("actions.cancel")}
              </button>
              <SaveButton
                disabled={
                  id
                    ? updateProductCardType.isLoading
                    : addProductCardType.isLoading
                }
              />
            </div>
          </form>
        </div>
      ) : (
        <BarLoading />
      )}
    </>
  );
};

export default ProductCardTypeCreate;
